import type { WebPulse } from './pulses';

// Which card the catch-up header shows in its morning-brief slot. Ported from the native apps'
// `resolveBriefState` (iOS `SymphonyHomeView` / Android's mirror) so web, iOS and Android pick the
// same state from the same inputs.

export type BriefState = 'loading' | 'hero' | 'newWorkspace' | 'activate' | 'none';

/** Well-known id of the seeded "Daily Team Meeting" routine — the morning-brief pulse. MUST match the
 *  native apps' `briefPulseIdentityId`. */
export const BRIEF_PULSE_IDENTITY_ID = '00000000-0000-0000-0000-000000000002';

/** True iff the brief routine exists and is not paused. */
export function isBriefPulseActive(pulses: WebPulse[]): boolean {
  return pulses.some((pulse) => pulse.id === BRIEF_PULSE_IDENTITY_ID && !pulse.paused);
}

/** True iff the brief routine has been SEEDED but is currently paused. A routine that was never seeded
 *  (absent from the list) is NOT paused — it's a brand-new account, handled by the new-workspace window,
 *  not the "Unlock morning meetings" lock card. */
export function isBriefPulsePaused(pulses: WebPulse[]): boolean {
  return pulses.some((pulse) => pulse.id === BRIEF_PULSE_IDENTITY_ID && pulse.paused);
}

/**
 * The header slot's state:
 *   • routines not fetched yet → `loading`, so no card flashes before we know the pulse state.
 *   • brief routine SEEDED but paused → `activate` ("Unlock morning meetings"). A routine that was never
 *     seeded is NOT this — it falls through to the new-workspace window below, because a brand-new account
 *     hasn't turned anything off yet.
 *   • brief not fetched yet → `loading`, so new-workspace can't flash and flip to the hero. Only a paused
 *     pulse short-circuits ahead of this — a known-paused routine resolves to `activate` whatever the brief
 *     request is doing, including never answering.
 *   • a real brief conversation → `hero` (the editorial headline).
 *   • no brief, but a workspace exists → `newWorkspace`. A brief fetch that FAILED is settled-with-no-brief,
 *     so it lands here rather than holding the slot blank. This is also where a never-seeded routine lands.
 *   • no brief, no workspace, but the workspaces fetch hasn't settled yet → `loading`. Workspace presence is
 *     what separates `newWorkspace` from `none` for a never-seeded routine, so we hold the skeleton instead
 *     of committing to `none` while it's still unknown (else a cold account flashes nothing then the card).
 *   • otherwise → `none`.
 */
export function resolveBriefState({
  pulsesLoaded,
  pulsePaused,
  briefSettled,
  hasBrief,
  hasWorkspace,
  workspacesSettled = true,
}: {
  pulsesLoaded: boolean;
  pulsePaused: boolean;
  briefSettled: boolean;
  hasBrief: boolean;
  hasWorkspace: boolean;
  workspacesSettled?: boolean;
}): BriefState {
  if (!pulsesLoaded) return 'loading';
  if (pulsePaused) return 'activate';
  if (!briefSettled) return 'loading';
  if (hasBrief) return 'hero';
  if (hasWorkspace) return 'newWorkspace';
  if (!workspacesSettled) return 'loading';
  return 'none';
}
